
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Download, X } from 'lucide-react';

// Define the BeforeInstallPromptEvent interface
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const PwaInstallBanner = () => {
  const { t } = useTranslation();
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Check if user has already dismissed or installed the app
    const isDismissed = localStorage.getItem('pwa-install-dismissed') === 'true';
    const isInstalled = localStorage.getItem('pwa-installed') === 'true';
    
    if (isDismissed || isInstalled) {
      return;
    }

    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: Event) => {
      // Prevent Chrome 76+ from automatically showing the prompt
      e.preventDefault();
      
      // Store the event for later use
      setInstallPrompt(e as BeforeInstallPromptEvent);
      setIsVisible(true);
      
      console.log('PWA is installable! BeforeInstallPromptEvent fired.');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Check if the app is already installed
    window.addEventListener('appinstalled', () => {
      localStorage.setItem('pwa-installed', 'true');
      setIsVisible(false);
      console.log('PWA was installed');
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstall = async () => {
    if (!installPrompt) return;
    
    // Show the install prompt
    await installPrompt.prompt();
    
    // Wait for the user to respond to the prompt
    const choiceResult = await installPrompt.userChoice;
    
    if (choiceResult.outcome === 'accepted') {
      console.log('User accepted the install prompt');
      setIsVisible(false);
    } else {
      console.log('User dismissed the install prompt');
    }
    
    // Clear the saved prompt as it can only be used once
    setInstallPrompt(null);
  };

  const handleDismiss = () => {
    setIsVisible(false);
    localStorage.setItem('pwa-install-dismissed', 'true');
  };

  if (!isVisible) {
    return null;
  }

  return (
    <Card className="fixed bottom-4 left-4 right-4 p-4 shadow-lg z-50 bg-white dark:bg-gray-800">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="bg-[#d32f2f] p-2 rounded-full">
            <Download className="h-5 w-5 text-white" />
          </div>
          <div>
            <h4 className="font-semibold text-sm">{t('common.installApp', 'Install Krispy Fan Club')}</h4>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t('common.installAppDesc', 'Add to your home screen for easier access')}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleDismiss}
          >
            <X className="h-4 w-4" />
          </Button>
          <Button 
            variant="default"
            size="sm"
            className="bg-[#d32f2f] hover:bg-[#b71c1c]"
            onClick={handleInstall}
          >
            {t('common.install', 'Install')}
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default PwaInstallBanner;
